/**
 * Compute Time-Weighted Return (TWR) from performance history.
 * 
 * TWR chains sub-period returns around capital events (deposits/withdrawals),
 * so adding or removing capital doesn't distort the investment return percentage.
 * 
 * Formula: TWR = [(1 + r1) * (1 + r2) * ... * (1 + rn)] - 1
 * where each ri is the return for the sub-period between capital events.
 */

interface PerformanceEntry {
  date: string;
  value: number;
  event?: string;
  event_amount?: number;
  [key: string]: unknown;
}

export function computeTWR(
  history: PerformanceEntry[],
  currentValue: number,
  totalInvested: number
): number {
  if (!history || history.length === 0) {
    // Fallback: simple return if no history
    return totalInvested > 0
      ? ((currentValue - totalInvested) / totalInvested) * 100
      : 0;
  }

  let twr = 1;
  let periodStartValue: number | null = null;

  for (const entry of history) {
    const isCapitalEvent = entry.event === 'deposit' || entry.event === 'withdrawal';

    if (isCapitalEvent) {
      // Close the current sub-period using the value just BEFORE the capital event
      if (periodStartValue !== null && periodStartValue > 0) {
        const eventAmount = entry.event_amount || 0;
        const valueBeforeEvent = entry.event === 'deposit'
          ? entry.value - eventAmount
          : entry.value + eventAmount;
        twr *= valueBeforeEvent / periodStartValue;
      }
      // New sub-period starts from the post-event value
      periodStartValue = entry.value;
    } else {
      if (periodStartValue === null) {
        // First non-event entry: this is the beginning of the first sub-period
        periodStartValue = entry.value;
      }
      // For non-event entries we just track the latest value;
      // the sub-period return is calculated when it ends (at next event or at current value)
    }
  }

  // Close the final sub-period with the live current value
  if (periodStartValue !== null && periodStartValue > 0) {
    twr *= currentValue / periodStartValue;
  }

  return (twr - 1) * 100;
}

/**
 * Parse raw performance_history from the database (could be any JSON shape)
 * into typed PerformanceEntry[].
 */
export function parsePerformanceHistory(raw: unknown): PerformanceEntry[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is PerformanceEntry =>
      typeof e === 'object' && e !== null && 'value' in e && 'date' in e
  );
}
